import React, {useState} from 'react';
import {Box, styled} from '@mui/material';
import {
  Body,
  designSystemToken,
  Icon,
  Label,
} from '@lightricks/react-design-system';
import {IconProps} from '@lightricks/react-design-system/dist/components/icon/IconProps';
import ActionsMenu, {
  MenuOption,
} from '@contentlab/components/actions-menu/ActionsMenu';

interface DropdownButtonProps {
  testID?: string;
  style?: React.CSSProperties;
  label: string;
  labelStyle?: React.CSSProperties;
  menuOptions: MenuOption[];
  selectedOptionId?: string | string[] | null;
  startIcon?: React.ReactNode;
  iconSize?: IconProps['size'];
  onClearButtonClick?: () => void;
  multiple?: boolean;
  showSelectedAmount?: boolean;
  onClick?: (element: HTMLElement) => void;
}

const shouldNotForwardProps = ['startIcon'];

const DropdownButtonContainer = styled(Box, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})(({startIcon}: {startIcon: boolean}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderRadius: '999px',
  padding: startIcon ? '8px 12px 8px 8px' : '8px 8px 8px 12px',
  cursor: 'pointer',
  height: '32px',
  boxSizing: 'border-box',
  userSelect: 'none',
  border: `1px solid ${designSystemToken('semantic.bg.neutral-inverse')}`,
}));

const SelectedCountContainer = styled(Box)(() => ({
  backgroundColor: designSystemToken('semantic.bg.neutral'),
  borderRadius: '100px',
  minWidth: '16px',
  height: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ClearButtonContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
}));

function DropdownButton({
  testID = 'dropdown-button',
  style = {},
  label,
  labelStyle = {},
  menuOptions,
  selectedOptionId,
  startIcon,
  iconSize = 'medium',
  onClearButtonClick,
  multiple = false,
  showSelectedAmount = false,
  onClick,
}: DropdownButtonProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(anchorEl);

  const selectedCount =
    multiple && Array.isArray(selectedOptionId) ? selectedOptionId.length : 0;

  const shouldShowAmount = showSelectedAmount && multiple && selectedCount > 0;

  const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    onClick?.(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <DropdownButtonContainer
        data-testid={testID}
        onClick={handleButtonClick}
        startIcon={!!startIcon}
        sx={style}
      >
        {startIcon}
        <Body
          size="md"
          color={designSystemToken('semantic.fg.primary')}
          sx={{paddingInline: '6px', ...labelStyle}}
        >
          {label}
        </Body>
        {shouldShowAmount && (
          <SelectedCountContainer>
            <Label size="xxs" color={designSystemToken('semantic.fg.white')}>
              {selectedCount}
            </Label>
          </SelectedCountContainer>
        )}
        {onClearButtonClick ? (
          <ClearButtonContainer
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              onClearButtonClick?.();
            }}
          >
            <Icon
              size={iconSize}
              appearance="neutral"
              name="Actions-Close-Small"
            />
          </ClearButtonContainer>
        ) : (
          <Icon
            size={iconSize}
            appearance="neutral"
            name="Actions-Arrow-Down-Small"
            style={{
              transform: menuOpen ? 'rotate(180deg)' : 'unset',
            }}
          />
        )}
      </DropdownButtonContainer>
      <ActionsMenu
        menuOpen={menuOpen}
        onCloseMenu={handleCloseMenu}
        anchorEl={anchorEl}
        menuOptions={menuOptions}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        selectedOptionId={selectedOptionId}
        multiple={multiple}
      />
    </>
  );
}

export default DropdownButton;
